/**
 * Article Chapters Web Component
 * Handles chapter navigation, heading ID generation, and scroll spy
 */

class ArticleChapters extends HTMLElement {
  constructor() {
    super();
    this.headings = [];
    this.navLinks = [];
    this.observer = null;
    this.currentActiveIndex = 0;
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  init() {
    this.body = this.querySelector('.article-main__body');
    this.nav = this.querySelector('.article-main__chapters');

    if (!this.body) return;

    // Process images first (before restructuring DOM)
    this.processImages();

    // Process inline citations like (1), (2), (1,4)
    this.processCitations();

    // Get all H2 headings from article content
    this.headings = Array.from(this.body.querySelectorAll('h2'));

    if (this.headings.length === 0) {
      // Hide nav if no chapters found
      if (this.nav) {
        this.nav.style.display = 'none';
      }
      return;
    }

    this.processHeadings();
    if (this.nav) {
      this.buildNavigation();
      this.setupScrollSpy();
    }
  }

  /**
   * Process inline citations: wrap (1), (2), (1,4) etc. in sup tags
   */
  processCitations() {
    // Get all text nodes within paragraphs
    const walker = document.createTreeWalker(
      this.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Skip if parent is already a sup, script, or style tag
          const parent = node.parentElement;
          if (parent && ['SUP', 'SUB', 'SCRIPT', 'STYLE', 'CODE', 'PRE'].includes(parent.tagName)) {
            return NodeFilter.FILTER_REJECT;
          }
          // Only process if contains citation pattern
          if (/\(\d+(?:,\s*\d+)*\)/.test(node.textContent)) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        }
      }
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }

    // Process each text node
    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      // Match patterns like (1), (2), (1,4), (3,4,5)
      const pattern = /\((\d+(?:,\s*\d+)*)\)/g;

      if (!pattern.test(text)) return;

      // Reset regex
      pattern.lastIndex = 0;

      // Create a document fragment to hold the new content
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let match;

      while ((match = pattern.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          fragment.appendChild(
            document.createTextNode(text.slice(lastIndex, match.index))
          );
        }

        // Create sup element for the citation
        const sup = document.createElement('sup');
        sup.className = 'article-main__citation';
        sup.textContent = match[0]; // Include parentheses
        fragment.appendChild(sup);

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text after last match
      if (lastIndex < text.length) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex))
        );
      }

      // Replace the text node with the fragment
      textNode.parentNode.replaceChild(fragment, textNode);
    });
  }

  /**
   * Process images: wrap in figure and add alt text as caption
   */
  processImages() {
    const images = this.body.querySelectorAll('img:not(figure img)');

    images.forEach(img => {
      const altText = img.getAttribute('alt');

      // Skip if no alt text or already wrapped in figure
      if (!altText || img.closest('figure')) return;

      // Create figure wrapper
      const figure = document.createElement('figure');
      figure.className = 'article-main__figure';

      // Create figcaption with alt text
      const figcaption = document.createElement('figcaption');
      figcaption.className = 'article-main__figcaption';
      figcaption.textContent = altText;

      // Wrap image in figure
      img.parentNode.insertBefore(figure, img);
      figure.appendChild(img);
      figure.appendChild(figcaption);
    });
  }

  /**
   * Generate a URL-friendly slug from text
   */
  slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-+|-+$/g, ''); // Trim hyphens from start/end
  }

  /**
   * Process H2 headings: add IDs, wrap content in chapter sections
   */
  processHeadings() {
    this.headings.forEach((heading, index) => {
      const chapterNum = index + 1;
      const headingText = heading.textContent.trim();
      const slug = this.slugify(headingText) || `chapter-${chapterNum}`;

      // Generate unique ID
      let id = slug;
      let counter = 1;
      while (document.getElementById(id)) {
        id = `${slug}-${counter}`;
        counter++;
      }

      // Set the ID on the heading
      heading.id = id;

      // Create chapter wrapper
      const chapterSection = document.createElement('section');
      chapterSection.className = 'article-chapter';
      chapterSection.setAttribute('data-chapter', chapterNum);

      // Create chapter header with number
      const chapterHeader = document.createElement('div');
      chapterHeader.className = 'article-chapter__header';
      chapterHeader.innerHTML = `
        <span class="article-chapter__number">${chapterNum}.</span>
        <h2 class="article-chapter__title" id="${id}">${headingText}</h2>
      `;

      // Create content container
      const contentContainer = document.createElement('div');
      contentContainer.className = 'article-chapter__content';

      // Move content between this H2 and next H2 into the content container
      let sibling = heading.nextElementSibling;
      const contentNodes = [];

      while (sibling && sibling.tagName !== 'H2') {
        contentNodes.push(sibling);
        sibling = sibling.nextElementSibling;
      }

      // Build the chapter section
      chapterSection.appendChild(chapterHeader);

      if (contentNodes.length > 0) {
        contentNodes.forEach(node => {
          contentContainer.appendChild(node);
        });
        chapterSection.appendChild(contentContainer);
      }

      // Insert chapter section where the heading was
      heading.parentNode.insertBefore(chapterSection, heading);

      // Remove the original heading (now replaced by chapter structure)
      heading.remove();

      // Update reference to the new heading element
      this.headings[index] = chapterSection.querySelector('.article-chapter__title');
    });
  }

  /**
   * Build the chapter navigation sidebar
   */
  buildNavigation() {
    const navList = document.createElement('ul');
    navList.className = 'article-main__chapters-list';

    this.headings.forEach((heading, index) => {
      const chapterNum = index + 1;
      const headingText = heading.textContent.trim();
      const headingId = heading.id;

      const listItem = document.createElement('li');
      listItem.className = 'article-main__chapters-item';

      const link = document.createElement('a');
      link.className = 'article-main__chapter-link';
      link.href = `#${headingId}`;
      link.innerHTML = `
        <span class="article-main__chapter-num">${chapterNum}.</span>
        <span class="article-main__chapter-title">${headingText}</span>
      `;

      // Smooth scroll on click
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.getElementById(headingId);
        if (target) {
          const headerOffset = parseInt(
            getComputedStyle(document.documentElement)
              .getPropertyValue('--total-header-timer-height') || '76'
          );
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset - 24;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Update active state immediately
          this.setActiveChapter(index);
        }
      });

      listItem.appendChild(link);
      navList.appendChild(listItem);

      this.navLinks.push(link);
    });

    this.nav.appendChild(navList);

    // Set first chapter as active initially
    if (this.navLinks.length > 0) {
      this.setActiveChapter(0);
    }
  }

  /**
   * Set up IntersectionObserver for scroll spy functionality
   */
  setupScrollSpy() {
    // Calculate root margin to account for fixed header
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--total-header-timer-height') || '76'
    );

    const options = {
      root: null,
      rootMargin: `-${headerHeight + 50}px 0px -50% 0px`,
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = this.headings.findIndex(h => h === entry.target);
          if (index !== -1) {
            this.setActiveChapter(index);
          }
        }
      });
    }, options);

    // Observe all chapter headings
    this.headings.forEach(heading => {
      this.observer.observe(heading);
    });

    // Also track scroll position for better accuracy
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateActiveOnScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /**
   * Update active chapter based on scroll position
   */
  updateActiveOnScroll() {
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--total-header-timer-height') || '76'
    );
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const threshold = headerHeight + 100;

    let activeIndex = 0;

    this.headings.forEach((heading, index) => {
      const rect = heading.getBoundingClientRect();
      const absoluteTop = rect.top + scrollTop;

      if (scrollTop >= absoluteTop - threshold) {
        activeIndex = index;
      }
    });

    if (activeIndex !== this.currentActiveIndex) {
      this.setActiveChapter(activeIndex);
    }
  }

  /**
   * Set the active chapter in the navigation
   */
  setActiveChapter(index) {
    this.currentActiveIndex = index;

    this.navLinks.forEach((link, i) => {
      if (i === index) {
        link.classList.add('is-active');
      } else {
        link.classList.remove('is-active');
      }
    });
  }
}

// Register the custom element
if (!customElements.get('article-chapters')) {
  customElements.define('article-chapters', ArticleChapters);
}
